import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Chip,
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  Divider,
  Alert
} from '@mui/material';
import {
  Group as GroupIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { ChatType, UserRole, User, ChatParticipant, ChatSettings } from '../../utils/types';
import { createChat, findPrivateChat } from '../../services/chatService';
import { getAllUsers } from '../../services/userService';

interface CreateChatDialogProps {
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  currentUserName: string;
  currentUserRole?: UserRole;
}

const CreateChatDialog: React.FC<CreateChatDialogProps> = ({
  open,
  onClose,
  currentUserId,
  currentUserName,
  currentUserRole
}) => {
  const [chatType, setChatType] = useState<ChatType>('private');
  const [chatName, setChatName] = useState('');
  const [chatDescription, setChatDescription] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // تحميل قائمة المستخدمين
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      const users = await getAllUsers();
      // استبعاد المستخدم الحالي
      const filteredUsers = users.filter(user => user.id !== currentUserId && user.isActive);
      setAvailableUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('فشل في تحميل قائمة المستخدمين');
    }
  };

  // إعادة تعيين النموذج
  const resetForm = () => {
    setChatType('private');
    setChatName('');
    setChatDescription('');
    setSelectedUsers([]);
    setError('');
  };

  // إغلاق النافذة
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // إنشاء المحادثة
  const handleCreateChat = async () => {
    if (loading) return;

    // التحقق من صحة البيانات
    if (chatType === 'private' && selectedUsers.length !== 1) {
      setError('يجب اختيار مستخدم واحد فقط للمحادثة الخاصة');
      return;
    }

    if (chatType !== 'private' && (!chatName.trim() || selectedUsers.length === 0)) {
      setError('يجب إدخال اسم المحادثة واختيار مستخدم واحد على الأقل');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // للمحادثات الخاصة، التحقق من وجود محادثة مسبقة
      if (chatType === 'private') {
        try {
          const existingChat = await findPrivateChat(currentUserId, selectedUsers[0].id);
          if (existingChat) {
            setError('توجد محادثة خاصة مسبقة مع هذا المستخدم');
            setLoading(false);
            return;
          }
        } catch (error) {
          console.log('Error checking existing chat:', error);
          // نتجاهل هذا الخطأ ونكمل إنشاء المحادثة
        }
      }

      // إعداد المشاركين
      const participants: ChatParticipant[] = [
        // المستخدم الحالي كمدير
        {
          userId: currentUserId,
          userName: currentUserName,
          userRole: currentUserRole || UserRole.CASHIER,
          joinedAt: new Date(),
          role: 'admin',
          permissions: {
            canSendMessages: true,
            canSendFiles: true,
            canDeleteMessages: true,
            canEditChat: true,
            canAddMembers: true,
            canRemoveMembers: true
          },
          lastSeen: new Date(),
          isOnline: true
        },
        // المستخدمين المختارين
        ...selectedUsers.map(user => ({
          userId: user.id,
          userName: user.name,
          userRole: user.role,
          joinedAt: new Date(),
          role: chatType === 'announcement' ? 'member' as const : 'moderator' as const,
          permissions: {
            canSendMessages: chatType !== 'announcement',
            canSendFiles: chatType !== 'announcement',
            canDeleteMessages: false,
            canEditChat: false,
            canAddMembers: chatType === 'group',
            canRemoveMembers: false
          },
          lastSeen: new Date(),
          isOnline: false
        }))
      ];

      // إعدادات المحادثة
      const settings: ChatSettings = {
        allowFileSharing: true,
        allowReactions: true,
        allowReplies: true,
        maxFileSize: 10, // 10 MB
        allowedFileTypes: ['image/*', 'application/pdf', 'text/*'],
        muteNotifications: false,
        autoDeleteMessages: false,
        autoDeleteDays: 30
      };

      // إنشاء المحادثة
      const chatData: any = {
        name: chatType === 'private' ? `${currentUserName} - ${selectedUsers[0].name}` : chatName.trim(),
        type: chatType,
        participants,
        createdBy: currentUserId,
        isActive: true,
        settings
      };

      // إضافة الوصف فقط إذا كان موجوداً وغير فارغ
      if (chatDescription && chatDescription.trim()) {
        chatData.description = chatDescription.trim();
      }

      console.log('Creating chat with data:', chatData);
      const chatId = await createChat(chatData);
      console.log('Chat created successfully with ID:', chatId);

      handleClose();
    } catch (error) {
      console.error('Error creating chat:', error);
      if (error instanceof Error) {
        setError(`فشل في إنشاء المحادثة: ${error.message}`);
      } else {
        setError('فشل في إنشاء المحادثة');
      }
    } finally {
      setLoading(false);
    }
  };

  // الحصول على عنوان النافذة
  const getDialogTitle = () => {
    switch (chatType) {
      case 'private':
        return 'إنشاء محادثة خاصة';
      case 'group':
        return 'إنشاء مجموعة';
      case 'announcement':
        return 'إنشاء قناة إعلانات';
      default:
        return 'إنشاء محادثة جديدة';
    }
  };

  // الحصول على وصف نوع المحادثة
  const getChatTypeDescription = () => {
    switch (chatType) {
      case 'private':
        return 'محادثة خاصة بينك وبين مستخدم آخر';
      case 'group':
        return 'مجموعة يمكن لجميع الأعضاء المشاركة فيها';
      case 'announcement':
        return 'قناة للإعلانات - المديرون فقط يمكنهم الإرسال';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {getDialogTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getChatTypeDescription()}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* نوع المحادثة */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
              نوع المحادثة
            </FormLabel>
            <RadioGroup
              value={chatType}
              onChange={(e) => setChatType(e.target.value as ChatType)}
              row
            >
              <FormControlLabel
                value="private"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">خاصة</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="group"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" />
                    <Typography variant="body2">مجموعة</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="announcement"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AnnouncementIcon fontSize="small" />
                    <Typography variant="body2">إعلانات</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* اسم المحادثة (للمجموعات والإعلانات) */}
          {chatType !== 'private' && (
            <TextField
              label="اسم المحادثة"
              value={chatName}
              onChange={(e) => setChatName(e.target.value)}
              fullWidth
              required
              placeholder="أدخل اسم المحادثة"
            />
          )}

          {/* وصف المحادثة (اختياري) */}
          {chatType !== 'private' && (
            <TextField
              label="الوصف (اختياري)"
              value={chatDescription}
              onChange={(e) => setChatDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="وصف مختصر للمحادثة"
            />
          )}

          {/* اختيار المستخدمين */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {chatType === 'private' ? 'اختر المستخدم' : 'اختر الأعضاء'}
            </Typography>
            
            <Autocomplete
              multiple={chatType !== 'private'}
              options={availableUsers}
              getOptionLabel={(option) => option.name}
              value={selectedUsers}
              onChange={(_, newValue) => {
                if (Array.isArray(newValue)) {
                  setSelectedUsers(newValue);
                } else {
                  setSelectedUsers(newValue ? [newValue] : []);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={chatType === 'private' ? 'ابحث عن مستخدم' : 'ابحث عن مستخدمين'}
                  size="small"
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    {chatType !== 'private' && (
                      <Checkbox checked={selected} />
                    )}
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {option.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.role} - {option.branchName}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    avatar={<Avatar sx={{ width: 24, height: 24 }}>{option.name.charAt(0)}</Avatar>}
                  />
                ))
              }
            />
          </Box>

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          إلغاء
        </Button>
        <Button
          onClick={handleCreateChat}
          variant="contained"
          disabled={loading || selectedUsers.length === 0}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'جاري الإنشاء...' : 'إنشاء'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateChatDialog;
